/**
 * Lightweight variable-height virtual list for the chat thread.
 *
 * Designed for the widget's constraints: no @angular/cdk dependency, works
 * inside Shadow DOM, and cooperates with a "stick to bottom while streaming"
 * policy. Heights are estimated until each row is measured; measurements are
 * cached by message id so recycled rows do not re-estimate.
 *
 * Estimation strategy (in priority order):
 *  1. Exact measured height for this message id
 *  2. Content-aware formula (role + text length + sources) blended with a
 *     running average of measured heights in the same bucket — so the model
 *     self-calibrates to the host's font metrics after a few rows render
 *  3. Static fallbacks tuned to the widget's SCSS (14px / 1.55–1.6 line-height)
 */

export interface VirtualItem {
  readonly id: string;
}

/** Shape the estimator needs — matches ChatMessage fields without importing it. */
export interface EstimableMessage extends VirtualItem {
  readonly role?: string;
  readonly content?: string;
  readonly status?: string;
  readonly sources?: readonly unknown[];
}

export interface VirtualWindow<T extends VirtualItem> {
  /** Items currently in the overscan range (to be rendered). */
  readonly items: readonly T[];
  /** Absolute start index of {@link items} in the full list. */
  readonly startIndex: number;
  /** Spacer above the rendered window (px). */
  readonly padTop: number;
  /** Spacer below the rendered window (px). */
  readonly padBottom: number;
  /** Total estimated/measured scroll height (px). */
  readonly totalHeight: number;
}

// ── Layout constants derived from rag-ask-widget.component.scss ───────────────
// .rag-msg padding 5+5, bubble padding 9+9, footer ~16, wrapper gap.
const CHROME_USER = 48; // outer padding + bubble chrome + footer
const CHROME_ASSISTANT = 52;
const LINE_HEIGHT = 22; // 13.5px * 1.6 ≈ 21.6
const AVG_CHAR_WIDTH = 7.2; // approximate at 13.5px system sans
const CONTENT_WIDTH = 340; // panel 420 − horizontal paddings − bubble inset
const CHARS_PER_LINE = Math.max(20, Math.floor(CONTENT_WIDTH / AVG_CHAR_WIDTH));

const SOURCE_LABEL = 18; // "Sources" heading
const SOURCE_ROW = 36; // one collapsed source card + gap
const SOURCE_BLOCK_MARGIN = 12; // margin-top of .rag-sources

/** Activate virtualisation only past this length — short threads stay simple. */
export const VIRTUAL_THRESHOLD = 24;

/** Extra rows rendered above/below the viewport. */
export const VIRTUAL_OVERSCAN = 4;

export type EstimateFn<T extends VirtualItem> = (item: T, index: number) => number;

type HeightBucket = 'user' | 'assistant' | 'assistant_sources' | 'error';

function bucketOf(item: EstimableMessage): HeightBucket {
  if (item.status === 'error') return 'error';
  if (item.role === 'user') return 'user';
  if (item.sources && item.sources.length > 0) return 'assistant_sources';
  return 'assistant';
}

/**
 * Count visual lines from plain text without allocating an array.
 * Blank lines and very long unbroken runs both produce extra height.
 */
function estimateLines(text: string): number {
  if (!text) return 1;
  let lines = 0;
  let col = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    if (c === 10 /* \n */) {
      lines++;
      col = 0;
      continue;
    }
    col++;
    if (col >= CHARS_PER_LINE) {
      lines++;
      col = 0;
    }
  }
  if (col > 0 || text.length === 0) lines++;
  // Markdown structural overhead (lists, headings, code fences) — a light bump
  // so we tend to over-estimate rather than under-estimate (under causes jumps).
  const structural = (text.match(/^#{1,6}\s|```|^\s*[-*+]\s|^\s*\d+[.)]\s/gm) ?? []).length;
  return Math.max(1, lines + Math.ceil(structural * 0.4));
}

/**
 * Content-aware height estimate. Prefer slight over-estimation: spacer
 * corrections after measure are less jarring when the scrollbar grows than
 * when it shrinks under the cursor.
 */
export function defaultEstimate(item: EstimableMessage): number {
  const content = item.content ?? '';
  const lines = estimateLines(content);
  const textHeight = lines * LINE_HEIGHT;

  if (item.status === 'error') {
    return CHROME_ASSISTANT + Math.max(LINE_HEIGHT, textHeight);
  }

  if (item.role === 'user') {
    return CHROME_USER + textHeight;
  }

  // Assistant: body + optional sources block (collapsed extracts only).
  let height = CHROME_ASSISTANT + textHeight;
  const sourceCount = item.sources?.length ?? 0;
  if (sourceCount > 0) {
    height += SOURCE_BLOCK_MARGIN + SOURCE_LABEL + sourceCount * SOURCE_ROW;
  }
  // Streaming cursor row is already covered by CHROME.
  return height;
}

/**
 * Mutable height cache + window calculator. One instance per widget.
 *
 * Also maintains per-bucket rolling averages of measured heights so unmeasured
 * rows of the same kind get better estimates after a few real samples land.
 */
export class VirtualListState<T extends VirtualItem> {
  private readonly heights = new Map<string, number>();
  private readonly estimate: EstimateFn<T>;

  /** Running sum/count of measured heights per bucket for adaptive estimates. */
  private readonly bucketSum: Record<HeightBucket, number> = {
    user: 0,
    assistant: 0,
    assistant_sources: 0,
    error: 0,
  };
  private readonly bucketCount: Record<HeightBucket, number> = {
    user: 0,
    assistant: 0,
    assistant_sources: 0,
    error: 0,
  };

  // Prefix-offset cache: rebuilt only when heights or list identity change.
  private cachedIds: readonly string[] | null = null;
  private cachedOffsets: number[] | null = null;
  private cachedTotal = 0;
  private heightsVersion = 0;
  private lastBuiltVersion = -1;

  constructor(estimate: EstimateFn<T> = defaultEstimate as EstimateFn<T>) {
    this.estimate = estimate;
  }

  /** Record a measured height for a rendered row. Returns true if it changed. */
  setHeight(id: string, height: number, item?: EstimableMessage): boolean {
    if (!Number.isFinite(height) || height <= 0) return false;
    const rounded = Math.ceil(height);
    const prev = this.heights.get(id);
    if (prev === rounded) return false;

    this.heights.set(id, rounded);
    this.heightsVersion++;

    // Fold into the bucket average. First measurement for an id contributes
    // fully; subsequent remeasures (e.g. streaming growth) adjust the sum.
    if (item) {
      const b = bucketOf(item);
      if (prev === undefined) {
        this.bucketSum[b] += rounded;
        this.bucketCount[b] += 1;
      } else {
        this.bucketSum[b] += rounded - prev;
      }
    }
    return true;
  }

  getHeight(item: T, index: number): number {
    const measured = this.heights.get(item.id);
    if (measured !== undefined) return measured;

    const formula = this.estimate(item, index);
    const b = bucketOf(item as unknown as EstimableMessage);
    const n = this.bucketCount[b];
    if (n >= 3) {
      // Blend formula with the observed average (70% measured, 30% formula)
      // so content length still influences the result while the average
      // absorbs host-specific font / zoom differences.
      const avg = this.bucketSum[b] / n;
      return Math.ceil(avg * 0.7 + formula * 0.3);
    }
    return formula;
  }

  /** Drop heights for ids no longer in the list (after reset / retry). */
  prune(ids: ReadonlySet<string>): void {
    let removed = false;
    for (const key of this.heights.keys()) {
      if (!ids.has(key)) {
        this.heights.delete(key);
        removed = true;
      }
    }
    if (removed) {
      this.heightsVersion++;
      this.cachedIds = null;
    }
  }

  clear(): void {
    this.heights.clear();
    this.heightsVersion++;
    this.cachedIds = null;
    this.cachedOffsets = null;
    for (const key of Object.keys(this.bucketCount) as HeightBucket[]) {
      this.bucketSum[key] = 0;
      this.bucketCount[key] = 0;
    }
  }

  /**
   * Compute the visible window for a given scroll position.
   *
   * Prefix offsets are cached across scroll events and only rebuilt when the
   * message list identity or any measured height changes.
   */
  window(
    items: readonly T[],
    scrollTop: number,
    viewportHeight: number,
    forceTail = false,
  ): VirtualWindow<T> {
    const n = items.length;
    if (n === 0) {
      return { items: [], startIndex: 0, padTop: 0, padBottom: 0, totalHeight: 0 };
    }

    const offsets = this.ensureOffsets(items);
    const totalHeight = this.cachedTotal;

    const viewTop = Math.max(0, scrollTop);
    const viewBottom = viewTop + Math.max(1, viewportHeight);

    // Binary search: first index whose bottom edge is past viewTop.
    let start = lowerBound(offsets, viewTop, n);
    // First index whose top edge is at or past viewBottom.
    let end = lowerBound(offsets, viewBottom, n);
    // Include the row that straddles viewBottom.
    if (end < n && offsets[end]! < viewBottom) end++;
    end = Math.min(n, Math.max(end, start + 1));

    start = Math.max(0, start - VIRTUAL_OVERSCAN);
    end = Math.min(n, end + VIRTUAL_OVERSCAN);

    // While streaming with stick-to-bottom, guarantee the tail is mounted so
    // the growing answer and its cursor stay in the DOM.
    if (forceTail) {
      const tailStart = Math.max(0, n - Math.max(VIRTUAL_OVERSCAN, 2));
      start = Math.min(start, tailStart);
      end = n;
    }

    return {
      items: items.slice(start, end),
      startIndex: start,
      padTop: offsets[start]!,
      padBottom: totalHeight - offsets[end]!,
      totalHeight,
    };
  }

  /**
   * Rebuild the prefix-sum array when the list or any height has changed.
   * Same list + same heights → reuse the previous array (scroll-hot path).
   */
  private ensureOffsets(items: readonly T[]): number[] {
    const n = items.length;
    const sameList =
      this.cachedIds !== null &&
      this.cachedIds.length === n &&
      this.lastBuiltVersion === this.heightsVersion &&
      listIdsMatch(this.cachedIds, items);

    if (sameList && this.cachedOffsets) {
      return this.cachedOffsets;
    }

    const offsets = new Array<number>(n + 1);
    offsets[0] = 0;
    for (let i = 0; i < n; i++) {
      offsets[i + 1] = offsets[i]! + this.getHeight(items[i]!, i);
    }

    this.cachedOffsets = offsets;
    this.cachedTotal = offsets[n]!;
    this.cachedIds = items.map((m) => m.id);
    this.lastBuiltVersion = this.heightsVersion;
    return offsets;
  }
}

/** First index i where offsets[i] > value (or n if none). offsets has length n+1. */
function lowerBound(offsets: number[], value: number, n: number): number {
  let lo = 0;
  let hi = n;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (offsets[mid]! <= value) lo = mid + 1;
    else hi = mid;
  }
  // offsets[lo] is the first prefix strictly greater than value, so the row
  // that contains `value` is lo - 1 (clamped).
  return Math.max(0, lo - 1);
}

function listIdsMatch(cached: readonly string[], items: readonly VirtualItem[]): boolean {
  for (let i = 0; i < cached.length; i++) {
    if (cached[i] !== items[i]!.id) return false;
  }
  return true;
}
